import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, LoaderCircle, LocateFixed, MapPin, RotateCcw, Search } from "lucide-react";
import { useOrigin, type OriginValue } from "../context/OriginContext";
import { searchAirports } from "../data/airports";
import { usePopover } from "../utilities/usePopover";
import styles from "./LocationPicker.module.scss";

export type OriginTone = 'pending' | 'guess' | 'detected' | 'chosen';

/** How far to trust the origin. A `guess` (IP lookup, or the stand-in
 * default when everything failed) is the one the traveller should check. */
export const originTone = (origin: OriginValue | null, loading: boolean): OriginTone => {
    if (!origin) return loading ? 'pending' : 'guess';
    if (origin.pinned) return 'chosen';
    if (origin.source === 'browser') return 'detected';
    return 'guess';
};

const SOURCE_NOTE: Record<OriginTone, (origin: OriginValue | null) => string> = {
    pending: () => 'Finding where you are…',
    guess: (origin) => origin?.source === 'ip'
        ? 'Guessed from your internet connection, so it can be off.'
        : "We couldn't find you. Pick your city below.",
    detected: () => "From your device's location.",
    chosen: (origin) => origin?.source === 'browser' ? "From your device's location. Saved." : 'Chosen by you. Saved.',
};

const DEVICE_ERROR = {
    denied: "Location access is off for this site. Allow it in your browser's settings, or search below.",
    unavailable: "Couldn't get your position. Search for your city instead.",
};

type LocationPanelProps = {
    /** Called once a location is set, so the host can close itself. */
    onDone?: () => void;
};

/**
 * The location chooser itself: what the app currently thinks, how it got
 * there, and the two ways to correct it. Lives inside the header popover
 * and the account menu alike.
 */
export const LocationPanel = ({ onDone }: LocationPanelProps) => {
    const { origin, loading, chooseAirport, locateWithDevice, resetToAutomatic } = useOrigin();
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [locating, setLocating] = useState(false);
    const [deviceError, setDeviceError] = useState<keyof typeof DEVICE_ERROR | null>(null);
    const listId = useId();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const results = useMemo(() => (query.trim() ? searchAirports(query, 6) : []), [query]);
    const tone = originTone(origin, loading);

    useEffect(() => setActive(0), [query]);

    // On a phone, focusing the field would throw the keyboard over the
    // panel before the traveller has read it.
    useEffect(() => {
        if (window.matchMedia?.('(pointer: fine)').matches) inputRef.current?.focus();
    }, []);

    const locateMe = async () => {
        setLocating(true);
        setDeviceError(null);
        const result = await locateWithDevice();
        setLocating(false);
        if (result === 'ok') onDone?.();
        else setDeviceError(result);
    };

    const pick = (index: number) => {
        const airport = results[index];
        if (!airport) return;
        chooseAirport(airport);
        setQuery('');
        onDone?.();
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!results.length) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((i) => (i + 1) % results.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((i) => (i - 1 + results.length) % results.length);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            pick(active);
        }
    };

    return (
        <div className={styles.panel}>
            <p className={styles.label}>Your location</p>
            <div className={styles.current}>
                <span className={`${styles.currentIcon} ${tone === 'guess' ? styles.currentIconGuess : ''}`}>
                    {tone === 'pending'
                        ? <LoaderCircle size={16} className={styles.spin} aria-hidden="true" />
                        : <MapPin size={16} aria-hidden="true" />}
                </span>
                <div className={styles.currentText}>
                    <span className={styles.currentCity}>
                        {origin ? origin.city : 'Locating…'}
                        {origin && <span className={styles.code}>{origin.airport}</span>}
                    </span>
                    <span className={`${styles.note} ${tone === 'guess' ? styles.noteGuess : ''}`}>
                        {SOURCE_NOTE[tone](origin)}
                    </span>
                </div>
            </div>

            <button type="button" className={styles.deviceBtn} onClick={locateMe} disabled={locating} aria-busy={locating}>
                {locating
                    ? <LoaderCircle size={16} className={styles.spin} aria-hidden="true" />
                    : <LocateFixed size={16} aria-hidden="true" />}
                {locating ? 'Finding you…' : 'Use my current location'}
            </button>
            {deviceError && <p className={styles.error} role="alert">{DEVICE_ERROR[deviceError]}</p>}

            <div className={styles.searchField}>
                <Search size={15} className={styles.searchIcon} aria-hidden="true" />
                <input
                    ref={inputRef}
                    className={styles.searchInput}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Search a city or airport"
                    aria-label="Search a city or airport"
                    role="combobox"
                    aria-expanded={results.length > 0}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-activedescendant={results.length ? `${listId}-${active}` : undefined}
                    autoComplete="off"
                    spellCheck={false}
                />
            </div>

            {query.trim() && (
                results.length ? (
                    <ul className={styles.results} id={listId} role="listbox" aria-label="Matching airports">
                        {results.map((airport, index) => (
                            <li
                                key={airport.code}
                                id={`${listId}-${index}`}
                                role="option"
                                aria-selected={index === active}
                                className={`${styles.result} ${index === active ? styles.resultActive : ''}`}
                                onMouseEnter={() => setActive(index)}
                                // Keeps focus in the field, so the list doesn't
                                // collapse under the pointer mid-click.
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => pick(index)}
                            >
                                {/* The airport's own name explains matches the city
                                    doesn't ("war" → Newark) and tells a city's
                                    airports apart. */}
                                <span className={styles.resultText}>
                                    <span className={styles.resultCity}>
                                        {airport.city}
                                        <span className={styles.code}>{airport.code}</span>
                                    </span>
                                    <span className={styles.resultName}>{airport.name}</span>
                                </span>
                                <span className={styles.resultCountry}>{airport.country}</span>
                                {origin?.airport === airport.code && <Check size={15} className={styles.resultCheck} aria-label="Current" />}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className={styles.empty}>No airport city matches “{query.trim()}”.</p>
                )
            )}

            {origin?.pinned && (
                <button type="button" className={styles.resetBtn} onClick={resetToAutomatic}>
                    <RotateCcw size={13} aria-hidden="true" /> Detect automatically instead
                </button>
            )}
        </div>
    );
};

type LocationPickerProps = {
    /** The host's own button style, so the trigger matches its row. */
    triggerClassName?: string;
    /** Lets the host hide the city name on narrow screens. */
    labelClassName?: string;
};

/** A pin button showing the traveller's location, opening LocationPanel. */
export const LocationPicker = ({ triggerClassName = '', labelClassName = '' }: LocationPickerProps) => {
    const { origin, loading } = useOrigin();
    const { open, toggle, close, triggerRef, popoverRef, position } = usePopover({ width: 340 });
    const tone = originTone(origin, loading);

    const city = origin?.city || (loading ? 'Locating…' : 'Set location');

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.trigger} ${triggerClassName}`}
                onClick={toggle}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={origin ? `Your location: ${origin.city}. Change location` : 'Set your location'}
                title={tone === 'guess' ? 'Is this right? Set your location' : undefined}
            >
                <span className={styles.triggerIcon}>
                    <MapPin size={16} aria-hidden="true" />
                    {tone === 'guess' && <span className={styles.guessDot} aria-hidden="true" />}
                </span>
                <span className={`${styles.triggerLabel} ${labelClassName}`}>{city}</span>
            </button>
            {open && position && createPortal(
                <div
                    ref={popoverRef}
                    className={styles.popover}
                    style={{ ...position }}
                    role="dialog"
                    aria-label="Your location"
                >
                    <LocationPanel onDone={() => close()} />
                </div>,
                document.body,
            )}
        </>
    );
};
